import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useTransition,
  memo,
} from "react";
import { axiosInstance, SERVER_ORIGIN } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import { FileText, Smile, Clock, Calendar, Paperclip, Tag } from "lucide-react";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import { getSocket } from "../socket";
import AssignTaskModal from "./AssignTaskModal";
import ProjectsSidebar from "./ProjectsSidebar";
import { useLocation, useSearchParams } from "react-router-dom";
import "./ProjectCommunication.css";
dayjs.extend(weekday);

const PAGE_SIZE = 80;
const FIRST_SNAPPY_BATCH = 30;

/* ---------- File icon (built in, no extra file needed) ---------- */
const FILE_ICONS = {
  pdf: "📕",
  doc: "📘",
  docx: "📘",
  xls: "📗",
  xlsx: "📗",
  csv: "📗",
  ppt: "📙",
  pptx: "📙",
  zip: "🗜️",
  rar: "🗜️",
  dwg: "📐",
  dxf: "📐",
  rvt: "🏗️",
  jpg: "🖼️",
  jpeg: "🖼️",
  png: "🖼️",
  gif: "🖼️",
  webp: "🖼️",
  txt: "📝",
};
const getFileIcon = (ext = "") => (
  <span className="pc-fileicon">{FILE_ICONS[String(ext).toLowerCase()] || "📄"}</span>
);

/* ---------- Project desktop notification helper ---------- */
const showProjectDesktopNotification = ({ message, project, myUserId }) => {
  try {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted"
    ) {
      return;
    }

    const senderId = String(
      message?.sender?._id || message?.senderId?._id || message?.senderId || "",
    );

    // Never notify for my own outgoing message.
    if (senderId && senderId === String(myUserId || "")) return;

    const senderName =
      message?.sender?.name ||
      message?.senderName ||
      message?.sender?.username ||
      message?.sender?.email ||
      message?.senderLabel ||
      "User";

    const projectId = String(
      message?.project?._id ||
        message?.projectId ||
        message?.project ||
        project?._id ||
        "",
    );

    const projectName =
      message?.project?.projectName ||
      message?.project?.name ||
      message?.projectName ||
      project?.projectName ||
      project?.name ||
      "Project Communication";

    const typedText = String(
      message?.content || message?.text || message?.message || "",
    ).trim();

    const preview = typedText
      ? typedText
      : message?.fileName
        ? `📎 ${message.fileName}`
        : message?.file
          ? "📎 Document received"
          : "New project message";

    const notification = new Notification(`${senderName} • ${projectName}`, {
      body: preview.slice(0, 180),
      icon: "/sdc-notification.png",
      badge: "/sdc-notification.png",
      tag: message?._id
        ? `project-message-${message._id}`
        : `project-message-${Date.now()}`,
      requireInteraction: false,
    });

    notification.onclick = () => {
      try {
        window.focus();
      } catch {}

      try {
        const currentPath =
          window.location.pathname || "/project-communication";

        const url = projectId
          ? `${currentPath}?projectId=${encodeURIComponent(projectId)}`
          : currentPath;

        window.location.assign(url);
      } catch {}

      try {
        notification.close();
      } catch {}
    };
  } catch (err) {
    console.error("Project notification error:", err);
  }
};

/* ---------- Image helpers (GLOBAL) ---------- */
const isImageFile = (url = "") =>
  /\.(jpg|jpeg|png|gif|webp)$/i.test(String(url));

// FRONTEND ONLY
const openPreview = async (fileKey, fileName = "") => {
  try {
    const url =
      `${SERVER_ORIGIN}/api/upload/proxy-download?url=` +
      `${encodeURIComponent(fileKey)}&name=${encodeURIComponent(fileName)}&inline=1`;
    window.open(url, "_blank");
  } catch (err) {
    console.error("Preview error:", err);
  }
};

/* ---------- date / size helpers ---------- */
const formatDateGroup = (date) => {
  const today = dayjs();
  const d = dayjs(date);
  if (d.isSame(today, "day")) return "Today";
  if (d.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  return d.format("dddd, MMM D");
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

const getFileSizeFromURL = async () => null;

/* ---------- tiny emoji list ---------- */
const EMOJIS = [
  "💼",
  "📊",
  "📈",
  "🗂️",
  "📝",
  "📅",
  "💻",
  "👍",
  "👏",
  "🙏",
  "🙌",
  "💪",
  "🔥",
  "🎉",
  "😀",
  "😁",
  "😂",
  "🤣",
  "😊",
  "😍",
  "😘",
  "🥰",
  "😎",
  "😇",
  "😉",
  "🤗",
  "🤩",
  "😅",
  "🙃",
  "😐",
  "😴",
  "🤔",
  "🙄",
  "😭",
  "🤝",
  "✨",
  "💯",
];

/* ---------- context menu ---------- */
const createContextMenu = (x, y, options) => {
  const existing = document.getElementById("context-menu");
  if (existing) existing.remove();
  const el = document.createElement("div");
  el.id = "context-menu";
  el.className = "pc-ctx";
  el.style.top = `${y}px`;
  el.style.left = `${x}px`;
  options.forEach((opt) => {
    const item = document.createElement("div");
    item.textContent = opt.label;
    item.className = "pc-ctx-item";
    item.onclick = () => {
      opt.action?.();
      el.remove();
    };
    el.appendChild(item);
  });
  document.body.appendChild(el);
  setTimeout(() => {
    const closer = () => el.remove();
    document.addEventListener("click", closer, { once: true });
  }, 0);
};

/* ===========================================================
      TYPE INFERENCE HELPERS (robust)
      =========================================================== */
const looksLikeDiscussionText = (text) =>
  typeof text === "string" && /^Topic:\s*/i.test(text || "");

// detect decisions even if server misses type/decision object
const looksLikeDecisionText = (text = "") => {
  if (typeof text !== "string") return false;
  return (
    /\bFinal Decision:\s*/i.test(text) ||
    /(^|\n)\s*Lock:\s*(true|false)/i.test(text) ||
    /(^|\n)\s*DecideBy:\s*\d{4}-\d{2}-\d{2}/i.test(text)
  );
};

const inferType = (m) => {
  if (m?.type === "employee-log") return "employee-log";
  if (/^Employee Entry:/i.test(m?.content || "")) return "employee-log";

  if (m?.type === "decision") return "decision";
  if (m?.decision) return "decision";
  if (looksLikeDecisionText(m?.content)) return "decision";

  if (m?.type === "discussion") return "discussion";
  if (looksLikeDiscussionText(m?.content)) return "discussion";

  return null;
};

/* ===========================================================
      PERFORMANCE: message cache + fast normalize (with meta)
      =========================================================== */
const fastNormalize = (raw) => {
  const trimmed = Array.isArray(raw) ? raw.slice(-PAGE_SIZE) : [];
  const byId = {};
  for (let i = 0; i < trimmed.length; i++) byId[trimmed[i]._id] = trimmed[i];

  const grouped = {};
  for (let i = 0; i < trimmed.length; i++) {
    const m = trimmed[i];
    const key = dayjs(m.createdAt).format("YYYY-MM-DD");

    const entryType = inferType(m);

    const entry = {
      _id: m._id,
      sender:
        m?.sender?.name ||
        m?.sender?.username ||
        m?.sender?.email ||
        m?.senderLabel ||
        "System",
      senderId: m?.sender?._id || null,
      text: m.content,
      file: m.file,
      fileName: m.fileName,
      fileSize: m.fileSize,
      type: entryType,
      task:
        m.task ||
        (m.type === "task"
          ? {
              assignedToId: m.assignedToId || m.assignedTo,
              assignedToName: m.assignedToName,
              typeofProject: m.typeofProject,
              workType: m.workType,
              drawingDocuments: m.drawingDocuments,
              estimatedHours: m.estimatedHours,
              dueDate: m.dueDate,
              remark: m.remark,
            }
          : null),
      decision: m.decision || null, // keep server decision object
      replyTo: m.replyTo
        ? {
            sender:
              byId[m.replyTo?._id || m.replyTo]?.sender?.name ||
              byId[m.replyTo?._id || m.replyTo]?.sender?.username ||
              byId[m.replyTo?._id || m.replyTo]?.sender?.email ||
              "Someone",
            text:
              byId[m.replyTo?._id || m.replyTo]?.content ||
              byId[m.replyTo?._id || m.replyTo]?.fileName ||
              "",
            _id:
              byId[m.replyTo?._id || m.replyTo]?._id ||
              m.replyTo?._id ||
              m.replyTo,
          }
        : null,
      time: new Date(m.createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      createdAt: m.createdAt,
    };

    if (!grouped[key]) grouped[key] = [entry];
    else grouped[key].push(entry);
  }
  return grouped;
};

/* ----------------------------- UI: Chips ----------------------------- */
const Pill = ({ icon, children, tone = "default" }) => (
  <span className={`pc-pill${tone !== "default" ? ` pc-pill--${tone}` : ""}`}>
    {icon ? <span style={{ display: "inline-flex" }}>{icon}</span> : null}
    {children}
  </span>
);

/* ============================ MENTIONS ============================ */
const makeMentionToken = (display, id) => `@{${display}|${id}}`;
const extractMentionIds = (text) => {
  const ids = [];
  const re = /\@\{([^|}]+)\|([^}]+)\}/g;
  let m;
  while ((m = re.exec(text))) ids.push(m[2]);
  return ids;
};
const matchMentionAtCursor = (text, caretPos) => {
  const slice = text.slice(0, caretPos);
  const m = /(^|\s)@([a-zA-Z0-9._-]{0,30})$/.exec(slice);
  if (!m) return null;
  return { start: slice.length - m[2].length - 1, query: m[2] || "" };
};
const renderWithMentions = (text) => {
  const parts = [];
  let last = 0;
  const re = /\@\{([^|}]+)\|([^}]+)\}/g;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={`${m.index}-${m[2]}`} className="pc-mention" title={`@${m[1]}`}>
        @{m[1]}
      </span>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};
const esc = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const displayToRaw = (text, members = []) => {
  if (!text) return "";
  const sorted = [...members].sort(
    (a, b) => (b.name || "").length - (a.name || "").length,
  );
  let out = text;
  for (const m of sorted) {
    if (!m?._id || !m?.name) continue;
    const rx = new RegExp(`@${esc(m.name)}(?![\\w})])`, "g");
    out = out.replace(rx, `@{${m.name}|${m._id}}`);
  }
  return out;
};

const MentionList = ({ open, style, items, activeIndex, onPick }) => {
  if (!open) return null;
  return (
    <div className="pc-mlist" style={style}>
      {items.length === 0 ? (
        <div className="pc-mlist-empty">No matches</div>
      ) : (
        items.map((m, i) => (
          <div
            key={m._id || i}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(m);
            }}
            className={`pc-mitem${i === activeIndex ? " pc-mitem--active" : ""}`}
          >
            <span className="pc-mavatar">
              {m.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
            <span style={{ fontWeight: 600 }}>{m.name}</span>
          </div>
        ))
      )}
    </div>
  );
};
/* ========================== /MENTIONS ========================== */

/* --------------------------- Task Card UI --------------------------- */
const TaskCard = ({ msg, isMe }) => {
  const t = msg.task || {};
  const assigneeName = t.assignedToName || t.assignedToId || "Member";

  return (
    <div className="pc-task">
      <div className="pc-task-head">
        <span className="pc-task-emoji" role="img" aria-label="task">
          🗒️
        </span>
        Task Assigned to {assigneeName}
      </div>

      {(t.typeofProject || t.workType) && (
        <div className="pc-task-pills">
          {t.typeofProject && (
            <Pill tone="soft" icon={<Tag size={14} />}>
              {t.typeofProject}
            </Pill>
          )}
          {t.workType && (
            <Pill tone="soft" icon={<Tag size={14} />}>
              {t.workType}
            </Pill>
          )}
        </div>
      )}

      {Array.isArray(t.drawingDocuments) && t.drawingDocuments.length > 0 && (
        <div className="pc-task-docs">
          <div className="pc-task-docs-title">
            <Paperclip size={12} />
            <span>Drawings &amp; Documents</span>
          </div>
          <div className="pc-task-docs-list">
            {t.drawingDocuments.map((d, i) => (
              <span key={i} className="pc-task-doc">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {t.remark ? (
        <div className="pc-task-remark">
          <span role="img" aria-label="remark">
            📝
          </span>
          <em style={{ fontStyle: "normal" }}>{t.remark}</em>
        </div>
      ) : null}

      <div className="pc-task-meta">
        {t.estimatedHours != null && (
          <div>
            <Clock size={14} />
            <span>Est: {Number(t.estimatedHours).toFixed(2)} h</span>
          </div>
        )}
        {t.dueDate && (
          <div>
            <Calendar size={14} />
            <span>
              Due:&nbsp;
              {dayjs(t.dueDate).isValid()
                ? dayjs(t.dueDate).format("DD MMM YYYY")
                : String(t.dueDate)}
            </span>
          </div>
        )}
      </div>

      <div className="pc-task-foot">
        <span>
          <strong>By:</strong> {isMe ? "You" : msg.sender}
        </span>
      </div>
    </div>
  );
};

/* --------------------------- Employee Log Card --------------------------- */
const EmployeeLogCard = ({ msg, isMe }) => {
  const lines = String(msg.text || "").split("\n");

  const getVal = (label) =>
    lines
      .find((l) => l.startsWith(label + ":"))
      ?.replace(label + ":", "")
      .trim() || "-";

  const Item = ({ icon, label, value }) => (
    <div className="pc-elog-item">
      <div className="pc-elog-icon">{icon}</div>
      <div style={{ flex: 1 }}>
        <div className="pc-elog-label">{label}</div>
        <div className="pc-elog-value">{value}</div>
      </div>
    </div>
  );

  return (
    <div className="pc-elog">
      <div className="pc-elog-head">
        <div>
          <div className="pc-elog-title">👨‍💻 Employee Work Entry</div>
          <div className="pc-elog-by">
            {isMe ? "Submitted by You" : msg.sender}
          </div>
        </div>
        <div className="pc-elog-time">{msg.time}</div>
      </div>

      <div className="pc-elog-body">
        <Item icon="📁" label="Project" value={getVal("Project")} />
        <Item icon="🏗️" label="Project Type" value={getVal("Type")} />
        <Item icon="📝" label="Work Type" value={getVal("Work")} />
        <Item icon="📄" label="Drawing" value={getVal("Drawing")} />
        <Item icon="🔄" label="Version" value={getVal("Version")} />

        <div className="pc-elog-hours">
          <div className="pc-elog-box pc-elog-box--est">
            <small>Estimated</small>
            <strong>{getVal("Estimated Hours")}</strong>
            <em>Hours</em>
          </div>
          <div className="pc-elog-box pc-elog-box--act">
            <small>Actual</small>
            <strong>{getVal("Actual Hours")}</strong>
            <em>Hours</em>
          </div>
        </div>

        <div className="pc-elog-when">
          <span>📅 {getVal("Date")}</span>
          <span>🕒 {getVal("Time")}</span>
        </div>

        {getVal("Remark") !== "-" && (
          <div className="pc-elog-remark">
            <b>Remark</b>
            <div>{getVal("Remark")}</div>
          </div>
        )}
      </div>
    </div>
  );
};

/* --------------------------- Reply Item --------------------------- */
const ReplyItem = ({ reply, isMe, ensureSize, triggerDownload }) => {
  const ext = reply.fileName?.split(".").pop()?.toLowerCase();
  return (
    <div
      className={`pc-reply${isMe ? " pc-reply--me" : ""}`}
      onMouseEnter={() => ensureSize(reply)}
    >
      <div className="pc-reply-head">
        {isMe ? "You" : reply.sender} • <span>{reply.time}</span>
      </div>

      {reply.file ? (
        <div className="pc-reply-file">
          {getFileIcon(ext)}
          <div className="pc-fgrow">
            <div className="pc-fname" title={reply.fileName}>
              {reply.fileName}
            </div>
            <div className="pc-fsize">{reply.fileSizeDisplay || "…"}</div>
          </div>
          <button
            onClick={() => triggerDownload(reply.file, reply.fileName)}
            className="pc-attach-dl"
            title="Download"
          >
            ⬇️
          </button>
        </div>
      ) : (
        <div className="pc-text">{renderWithMentions(reply.text || "")}</div>
      )}
    </div>
  );
};

/* ----------------------- Discussion helpers & UI ----------------------- */
const parseDiscussion = (text = "") => {
  const first = (text.split("\n")[0] || "").trim();
  const m = /^Topic:\s*(.+)$/i.exec(first);
  return {
    topic: m ? m[1] : null,
    body: m ? text.split("\n").slice(2).join("\n") : text,
  };
};

/* --------------------------- Discussion Card --------------------------- */
const DiscussionCard = ({
  msg,
  onReply,
  replies,
  authUserId,
  ensureSize,
  triggerDownload,
}) => {
  const { topic, body } = useMemo(
    () => parseDiscussion(msg.text || ""),
    [msg.text],
  );

  return (
    <div className="pc-disc">
      <div className="pc-disc-head">
        <Pill tone="violet">🗣️ Discussion</Pill>
        <small>{msg.time}</small>
      </div>

      {topic && <div className="pc-disc-topic">{topic}</div>}
      {body && <div className="pc-disc-body">{renderWithMentions(body)}</div>}

      {msg.file && (
        <div className="pc-attach" onMouseEnter={() => ensureSize(msg)}>
          {getFileIcon(msg.fileName?.split(".").pop()?.toLowerCase())}
          <div className="pc-fgrow">
            <div className="pc-fname" title={msg.fileName}>
              {msg.fileName}
            </div>
            <div className="pc-fsize">{msg.fileSizeDisplay || "…"}</div>
          </div>
          <button
            onClick={() => triggerDownload(msg.file, msg.fileName)}
            className="pc-attach-dl"
            title="Download"
          >
            ⬇️
          </button>
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        {replies.map((r) => (
          <ReplyItem
            key={r._id}
            reply={r}
            isMe={String(r.senderId || "") === String(authUserId || "")}
            ensureSize={ensureSize}
            triggerDownload={triggerDownload}
          />
        ))}
      </div>

      <div className="pc-disc-foot">
        <button
          type="button"
          onClick={onReply}
          className="pc-disc-reply"
          title="Reply to this discussion"
        >
          Reply
        </button>

        <span className="pc-disc-by">
          —{" "}
          {String(msg.senderId || "") === String(authUserId || "")
            ? "You"
            : msg.sender}
        </span>
      </div>
    </div>
  );
};

/* --------------------------- Decision Card --------------------------- */
const DecisionCard = ({ msg, isMe, ensureSize, triggerDownload }) => {
  const text = msg.text || "";
  const d = msg.decision || {};

  const topic = d.topic || /^Topic:\s*(.+)$/im.exec(text)?.[1]?.trim() || "";
  const finalDecision =
    d.finalDecision ||
    (
      /^Final Decision:\s*([\s\S]*?)(?:\n(?:DecideBy:|Lock:)|$)/im.exec(
        text,
      )?.[1] || ""
    ).trim();
  const decideByRaw =
    d.decideBy ||
    /^DecideBy:\s*([0-9]{4}-[0-9]{2}-[0-9]{2})/im.exec(text)?.[1] ||
    "";
  const decideBy =
    decideByRaw && dayjs(decideByRaw).isValid()
      ? dayjs(decideByRaw).format("DD MMM YYYY")
      : "";
  const lock = d.lock === true || /(^|\n)\s*Lock:\s*true/i.test(text);
  const byName = d.byName || msg.sender || (isMe ? "You" : "") || "—";
  const lockedBy = lock ? d.lockedBy || byName || "—" : "—";

  const ext = msg.fileName?.split(".").pop()?.toLowerCase();

  return (
    <div className="pc-dec">
      <div className="pc-dec-head">
        <b>🧭 Decision</b>
        <small>{msg.time}</small>
      </div>

      <div className="pc-dec-label">Topic:</div>
      <div className="pc-dec-field">{topic || "—"}</div>

      <div className="pc-dec-by">
        By:<span>{byName}</span>
      </div>

      <div className="pc-dec-label">Final Decision:</div>
      <div className="pc-dec-field">{finalDecision || "—"}</div>

      {msg.file && (
        <div className="pc-attach" onMouseEnter={() => ensureSize(msg)}>
          {getFileIcon(ext)}
          <div className="pc-fgrow">
            <div className="pc-fname" title={msg.fileName}>
              {msg.fileName}
            </div>
            <div className="pc-fsize">{msg.fileSizeDisplay || "…"}</div>
          </div>
          <button
            onClick={() => triggerDownload(msg.file, msg.fileName)}
            className="pc-attach-dl"
            title="Download"
          >
            ⬇️
          </button>
        </div>
      )}

      <div className="pc-chiprow">
        <span className="pc-chip pc-chip--blue">
          <Calendar size={14} /> Decide by: {decideBy || "—"}
        </span>
        <span
          className={`pc-chip ${lock ? "pc-chip--green" : "pc-chip--orange"}`}
        >
          🔒 {lock ? "Locked" : "Unlocked"}
        </span>
        <span className="pc-chip pc-chip--slate">
          Lock by: {lock ? lockedBy : "—"}
        </span>
      </div>

      <div className="pc-dec-foot">{msg.time}</div>
    </div>
  );
};

/* --------------------------- Message Row --------------------------- */
const MessageRow = memo(function MessageRow({
  msg,
  isMe,
  isMobile,
  onContext,
  ensureSize,
  triggerDownload,
  authUserId,
  onReply,
  threadReplies = [],
}) {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!msg.file || !isImageFile(msg.fileName)) return;

    let active = true;

    const loadImage = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `${SERVER_ORIGIN}/api/download?key=${encodeURIComponent(msg.file)}`,
          {
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!res.ok) {
          setImageUrl("FILE_NOT_FOUND");
          return;
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);

        if (active) setImageUrl(url);
      } catch (err) {
        console.error("Image load failed", err);
      }
    };

    loadImage();

    return () => {
      active = false;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.file]);

  const looksLikeDiscussionFlag =
    msg.type === "discussion" ||
    (looksLikeDiscussionText(msg.text) && !looksLikeDecisionText(msg.text));

  const longPress = (cb) => {
    let timer = null;
    return {
      onTouchStart: (e) => {
        timer = setTimeout(() => cb(e), 550);
      },
      onTouchEnd: () => clearTimeout(timer),
      onTouchMove: () => clearTimeout(timer),
    };
  };

  const pressHandlers = longPress((touchEvt) => {
    const t = touchEvt.touches?.[0];
    if (!t) return;
    onContext(
      { preventDefault: () => {}, clientX: t.clientX, clientY: t.clientY },
      msg,
      isMe,
    );
  });

  const wasIMentioned =
    (msg.text || "").includes("@{") &&
    (msg.text || "").includes("|") &&
    (msg.text || "").includes("}") &&
    /@\{[^|}]+\|([^}]+)\}/.test(msg.text || "") &&
    RegExp.$1 === String(authUserId || "");

  const isLockedDecision =
    msg.type === "decision" &&
    (msg.decision?.lock === true || /Lock:\s*true/i.test(msg.text || ""));

  const bubbleClass = [
    "pc-bubble",
    isMe ? "pc-bubble--me" : "",
    isMobile ? "pc-bubble--mobile" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`pc-row${isMe ? " pc-row--me" : ""}`}>
      <div
        className={bubbleClass}
        onContextMenu={(e) => onContext(e, msg, isMe)}
        {...pressHandlers}
      >
        {msg.replyTo && !looksLikeDiscussionFlag && (
          <div className="pc-quote">
            <strong>{msg.replyTo.sender}:</strong>{" "}
            {msg.replyTo.text || msg.replyTo.fileName}
          </div>
        )}

        {msg.type === "task" || msg.task ? (
          <TaskCard msg={msg} isMe={isMe} />
        ) : msg.type === "employee-log" ? (
          <EmployeeLogCard msg={msg} isMe={isMe} />
        ) : msg.type === "decision" ? (
          <DecisionCard
            msg={msg}
            isMe={isMe}
            ensureSize={ensureSize}
            triggerDownload={triggerDownload}
          />
        ) : looksLikeDiscussionFlag && !msg.replyTo ? (
          <DiscussionCard
            msg={msg}
            onReply={() => onReply(msg)}
            replies={threadReplies}
            authUserId={authUserId}
            ensureSize={ensureSize}
            triggerDownload={triggerDownload}
          />
        ) : msg.file ? (
          <div className={`pc-filewrap${isMe ? " pc-filewrap--me" : ""}`}>
            {/* ================= IMAGE FILE ================= */}
            {isImageFile(msg.fileName) ? (
              <div
                className={`pc-filecard pc-filecard--image${
                  isMe ? " pc-filecard--me" : ""
                }`}
                onClick={() => openPreview(msg.file)}
              >
                {imageUrl === "FILE_NOT_FOUND" ? (
                  <div className="pc-img-missing">⚠️ File not found in S3</div>
                ) : imageUrl ? (
                  <img src={imageUrl} alt="" className="pc-img" />
                ) : (
                  <div className="pc-img-ph" />
                )}
              </div>
            ) : msg.fileName?.toLowerCase().endsWith(".pdf") ? (
              /* ================= PDF FILE ================= */
              <div className={`pc-filecard${isMe ? " pc-filecard--me" : ""}`}>
                <div className="pc-pdf">
                  <div className="pc-pdf-badge">PDF</div>
                  <div className="pc-fgrow">
                    <div className="pc-fname" title={msg.fileName}>
                      {msg.fileName}
                    </div>
                    <div className="pc-fsize">
                      {msg.fileSizeDisplay || formatFileSize(msg.fileSize)}
                    </div>
                  </div>
                </div>

                <div className="pc-fileactions">
                  <button
                    onClick={() => openPreview(msg.file)}
                    className="pc-linkbtn"
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => triggerDownload(msg.file, msg.fileName)}
                    className="pc-linkbtn"
                  >
                    Download
                  </button>
                </div>
              </div>
            ) : (
              /* ================= OTHER FILES ================= */
              <div className={`pc-filecard${isMe ? " pc-filecard--me" : ""}`}>
                <div className="pc-pdf">
                  {getFileIcon(ext(msg.fileName))}
                  <div className="pc-fgrow">
                    <div className="pc-fname" title={msg.fileName}>
                      {msg.fileName}
                    </div>
                    <div className="pc-fsize">
                      {msg.fileSizeDisplay || formatFileSize(msg.fileSize)}
                    </div>
                  </div>
                </div>

                <div className="pc-fileactions">
                  <button
                    onClick={() => triggerDownload(msg.file, msg.fileName)}
                    className="pc-linkbtn"
                  >
                    ⬇ Download
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="pc-sender">
              {String(msg.senderId || "") === String(authUserId || "")
                ? "You"
                : msg.sender}
              {isLockedDecision && (
                <span className="pc-badge pc-badge--lock" title="Locked">
                  🔒 Locked
                </span>
              )}
              {wasIMentioned && (
                <span
                  className="pc-badge pc-badge--mention"
                  title="You were mentioned"
                >
                  🔔 mentioned
                </span>
              )}
            </div>
            <div className="pc-text">{renderWithMentions(msg.text || "")}</div>
          </>
        )}

        <div className="pc-time">{msg.time}</div>
      </div>
    </div>
  );
});

// small helper used by "other files" card
const ext = (name = "") => String(name).split(".").pop()?.toLowerCase();

/* ============================= Page ============================= */
const ProjectCommunication = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const userNavigatedBack = useRef(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Compose: display & raw
  const [message, setMessage] = useState("");
  const [messageRaw, setMessageRaw] = useState("");

  const [postAsDiscussion, setPostAsDiscussion] = useState(false);
  const [discussionTopic, setDiscussionTopic] = useState("");

  // Decision compose state
  const [postAsDecision, setPostAsDecision] = useState(false);
  const [decisionTopic, setDecisionTopic] = useState("");
  const [decisionFinal, setDecisionFinal] = useState("");
  const [decisionDecideBy, setDecisionDecideBy] = useState("");
  const [decisionLock, setDecisionLock] = useState(false);
  const [decisionFile, setDecisionFile] = useState(null);

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [visibleProjects, setVisibleProjects] = useState([]);

  const [, startTransition] = useTransition();
  const { authUser } = useAuthStore();

  // refs
  const fileInputRef = useRef();
  const inputRef = useRef();
  const messagesEndRef = useRef();
  const rootRef = useRef(null);
  const emojiRef = useRef(null);

  // UI
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Mentions UI
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionMenuPos, setMentionMenuPos] = useState({
    right: 92,
    bottom: 56,
  });

  // Members for mentions
  const [members, setMembers] = useState([]);

  // dnd state
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  // responsive
  const [containerW, setContainerW] = useState(1200);
  useEffect(() => {
    if (!rootRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 1200;
      setContainerW(w);
    });
    ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, []);
  const isMobile = containerW <= 768;
  const isDesktop = containerW >= 1024;

  const projectMembers = useMemo(() => members, [members]);

  // Router
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const incomingProjectId = useMemo(() => {
    return (
      location?.state?.openProjectId ||
      searchParams.get("projectId") ||
      searchParams.get("pid") ||
      null
    );
  }, [location, searchParams]);
  const incomingProjectName = useMemo(() => {
    const n =
      location?.state?.openProjectName ||
      searchParams.get("project") ||
      searchParams.get("name") ||
      null;
    return n ? String(n).trim() : null;
  }, [location, searchParams]);

  const normalizeName = (s) =>
    String(s || "")
      .trim()
      .toLowerCase();
  const normName = (p) => normalizeName(p?.projectName || p?.name);

  // ---------- CACHE + FETCH ----------
  const cacheRef = useRef(new Map());
  const fileSizeCache = useRef(new Map());
  const pendingFetchRef = useRef(new Map());

  const applyFileSizes = useCallback((grouped) => {
    const out = {};
    for (const dayKey of Object.keys(grouped)) {
      out[dayKey] = grouped[dayKey].map((m) => {
        const size = fileSizeCache.current.get(m._id);
        return size ? { ...m, fileSizeDisplay: size } : m;
      });
    }
    return out;
  }, []);

  // ---------- Assign Task: modal + immediate inject ----------
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);

  const pushTaskToChat = useCallback(
    (projectId, taskMsg) => {
      if (!projectId || !taskMsg) return;

      const createdAtISO = taskMsg.createdAt || new Date().toISOString();
      const key = dayjs(createdAtISO).format("YYYY-MM-DD");

      const entry = {
        _id: taskMsg._id || `temp-task-${Date.now()}`,
        sender:
          taskMsg?.sender?.name ||
          taskMsg?.sender?.username ||
          taskMsg?.sender?.email ||
          "System",
        senderId: taskMsg?.sender?._id || null,
        text: taskMsg.content || "",
        type: "task",
        task: taskMsg.task || {
          assignedToId: taskMsg.assignedToId,
          assignedToName: taskMsg.assignedToName,
          typeofProject: taskMsg.typeofProject,
          workType: taskMsg.workType,
          drawingDocuments: taskMsg.drawingDocuments,
          estimatedHours: taskMsg.estimatedHours,
          dueDate: taskMsg.dueDate,
          remark: taskMsg.remark,
        },
        decision: null,
        replyTo: null,
        time: new Date(createdAtISO).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        createdAt: createdAtISO,
      };

      const grouped = cacheRef.current.get(projectId) || {};
      const arr = grouped[key] ? [...grouped[key], entry] : [entry];
      grouped[key] = arr.slice(-PAGE_SIZE);
      cacheRef.current.set(projectId, grouped);

      if (
        selectedProject &&
        String(selectedProject._id) === String(projectId)
      ) {
        setChatMessages(applyFileSizes(grouped));
        requestAnimationFrame(() =>
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }),
        );
      }
    },
    [selectedProject, applyFileSizes],
  );

  const handleTaskAssigned = useCallback(
    (createdTaskMsg) => {
      setAssignTaskOpen(false);

      if (createdTaskMsg && selectedProject?._id) {
        pushTaskToChat(String(selectedProject._id), createdTaskMsg);
      }
    },
    [selectedProject?._id, pushTaskToChat],
  );

  // clear selection when this page unmounts
  useEffect(() => {
    return () => {
      setSelectedProject(null);
    };
  }, []);

  // close emoji on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!emojiRef.current) return;
      if (!emojiRef.current.contains(e.target)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // admin project connecter — re-select reliably when query/state changes
  useEffect(() => {
    if (!projects.length) return;

    const curId = selectedProject?._id ? String(selectedProject._id) : null;

    let next = null;

    if (incomingProjectId && String(incomingProjectId) !== curId) {
      next =
        projects.find((p) => String(p._id) === String(incomingProjectId)) ||
        null;
    }

    if (!next && incomingProjectName) {
      const target = incomingProjectName.trim().toLowerCase();
      next =
        projects.find(
          (p) =>
            (p.projectName || p.name || "").trim().toLowerCase() === target,
        ) ||
        projects.find((p) =>
          (p.projectName || p.name || "").trim().toLowerCase().includes(target),
        ) ||
        null;

      if (next && String(next._id) === curId) next = null;
    }

    if (next && !userNavigatedBack.current) {
      setSelectedProject(next);
    }
  }, [
    projects,
    incomingProjectId,
    incomingProjectName,
    selectedProject?._id, // eslint-disable-line
  ]);

  const setFromCacheImmediately = useCallback(
    (projectId) => {
      const cached = cacheRef.current.get(projectId);
      if (cached) {
        setChatMessages(applyFileSizes(cached));
        setMessagesLoading(false);
      } else {
        setChatMessages({});
        setMessagesLoading(true);
      }
    },
    [applyFileSizes],
  );

  // Progressive fetch
  const fetchMessages = useCallback(
    async (projectId, { skipIfCached = false } = {}) => {
      if (!projectId) return;
      if (skipIfCached && cacheRef.current.has(projectId)) return;

      const prev = pendingFetchRef.current.get(projectId);
      prev?.abort();

      const controllerSmall = new AbortController();
      pendingFetchRef.current.set(projectId, controllerSmall);

      try {
        const small = await axiosInstance.get(
          `/project-messages/${projectId}/messages?limit=${FIRST_SNAPPY_BATCH}`,
          { signal: controllerSmall.signal },
        );
        const groupedSmall = fastNormalize(small.data || []);
        cacheRef.current.set(projectId, groupedSmall);

        setChatMessages((prev) => {
          if (
            !selectedProject ||
            String(selectedProject._id) !== String(projectId)
          )
            return prev;
          return applyFileSizes(groupedSmall);
        });
        if (
          selectedProject &&
          String(selectedProject._id) === String(projectId)
        ) {
          setMessagesLoading(false);
        }

        const controllerFull = new AbortController();
        pendingFetchRef.current.set(projectId, controllerFull);

        const full = await axiosInstance.get(
          `/project-messages/${projectId}/messages?limit=${PAGE_SIZE}`,
          { signal: controllerFull.signal },
        );
        const groupedFull = fastNormalize(full.data || []);
        cacheRef.current.set(projectId, groupedFull);

        setChatMessages((prev) => {
          if (
            !selectedProject ||
            String(selectedProject._id) !== String(projectId)
          )
            return prev;
          return applyFileSizes(groupedFull);
        });
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          console.error("fetchMessages error:", e);
        }
      } finally {
        pendingFetchRef.current.delete(projectId);
      }
    },
    [selectedProject, applyFileSizes],
  );

  const prefetchVisible = useCallback(() => {
    const ids = (visibleProjects || [])
      .slice(0, 4)
      .map((p) => p._id)
      .filter(Boolean);
    ids.forEach((id) => fetchMessages(id, { skipIfCached: true }));
  }, [visibleProjects, fetchMessages]);

  /* ---------- initial projects ---------- */
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/projects");
      const list = Array.isArray(res.data) ? res.data : [];
      setProjects(list);
      setVisibleProjects(list);
    } catch (e) {
      console.error("Error fetching projects:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // choose initial selected project (ID -> Name -> last)
  useEffect(() => {
    if (!projects.length || selectedProject || userNavigatedBack.current)
      return;

    if (incomingProjectId) {
      const byId = projects.find(
        (proj) => String(proj._id) === String(incomingProjectId),
      );
      if (byId) {
        setSelectedProject(byId);
        return;
      }
    }

    if (incomingProjectName) {
      const target = normalizeName(incomingProjectName);
      const byName =
        projects.find((p) => normName(p) === target) ||
        projects.find((p) => normName(p).includes(target));
      if (byName) {
        setSelectedProject(byName);
        return;
      }
    }

    // Fallback only for desktop, not mobile
    if (!isMobile) {
      const lastId = localStorage.getItem("lastProjectId");
      if (lastId) {
        const last = projects.find(
          (proj) => String(proj._id) === String(lastId),
        );
        if (last) setSelectedProject(last);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projects,
    selectedProject,
    incomingProjectId,
    incomingProjectName,
    isMobile,
  ]);

  useEffect(() => {
    if (selectedProject?._id) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    }
  }, [selectedProject?._id]);

  useEffect(() => {
    prefetchVisible();
  }, [prefetchVisible]);

  useEffect(() => {
    const onFocus = () => {
      fetchProjects();
      prefetchVisible();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchProjects, prefetchVisible]);

  /* ---------- selection / load ---------- */
  useEffect(() => {
    if (!selectedProject?._id) {
      setMembers([]);
      return;
    }
    setFromCacheImmediately(selectedProject._id);
    fetchMessages(selectedProject._id);
  }, [selectedProject?._id, setFromCacheImmediately, fetchMessages]);

  /* ==========================================================
     PROJECT NOTIFICATION RECIPIENT IDS
  ========================================================== */
  const notificationRecipientIds = useMemo(() => {
    const myId = String(authUser?._id || "");

    return (members || [])
      .map((member) => String(member?._id || ""))
      .filter((id) => id && id !== myId);
  }, [members, authUser?._id]);

  // FETCH MEMBERS for mentions
  useEffect(() => {
    if (!selectedProject?._id) return setMembers([]);

    let cancelled = false;

    const normalize = (arr = []) =>
      (arr || [])
        .map((m) => ({
          _id:
            m?.user?._id ||
            m?.userId?._id ||
            m?.userId ||
            m?.employee?._id ||
            m?.employeeId?._id ||
            m?.employeeId ||
            m?._id ||
            m?.id ||
            "",
          name:
            m.name ||
            m.fullName ||
            m.displayName ||
            m.username ||
            m.email ||
            "Member",
        }))
        .filter((x) => x._id)
        .reduce((acc, cur) => {
          if (!acc.find((a) => a._id === cur._id)) acc.push(cur);
          return acc;
        }, []);

    // STEP 1: Get members DIRECTLY from project
    const projectMembersRaw =
      selectedProject.assignedUsers ||
      selectedProject.members ||
      selectedProject.participants ||
      selectedProject.users ||
      [];

    const normalizedMembers = normalize(projectMembersRaw);

    if (normalizedMembers.length) {
      if (!cancelled) setMembers(normalizedMembers);
      return;
    }

    // STEP 2: Fallback → infer from messages in cache
    const inferred = [];
    const seen = new Set();

    const grouped = cacheRef.current.get(selectedProject._id) || {};

    Object.values(grouped).forEach((day) =>
      day.forEach((m) => {
        const id = m.senderId || "";
        const name = m.sender || "";
        if (id && !seen.has(id)) {
          seen.add(id);
          inferred.push({ _id: id, name });
        }

        const t = m.task;
        if (t?.assignedToId && t?.assignedToName && !seen.has(t.assignedToId)) {
          seen.add(t.assignedToId);
          inferred.push({ _id: t.assignedToId, name: t.assignedToName });
        }
      }),
    );

    if (!cancelled) setMembers(inferred);

    return () => {
      cancelled = true;
    };
  }, [selectedProject]);

  // auto-scroll on updates
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (!messagesEndRef.current) return;

    if (firstLoadRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      firstLoadRef.current = false;
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  useEffect(() => {
    if (selectedProject?._id) {
      firstLoadRef.current = true;
    }
  }, [selectedProject?._id]);

  /* ---------- realtime: project assign/remove ---------- */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onAdded = (p) => {
      setProjects((prev) =>
        prev.some((x) => String(x._id) === String(p._id)) ? prev : [p, ...prev],
      );
      setVisibleProjects((prev) =>
        prev.some((x) => String(x._id) === String(p._id)) ? prev : [p, ...prev],
      );
    };

    const onRemoved = ({ _id }) => {
      setProjects((prev) => prev.filter((x) => String(x._id) !== String(_id)));
      setVisibleProjects((prev) =>
        prev.filter((x) => String(x._id) !== String(_id)),
      );
      setSelectedProject((sp) =>
        sp && String(sp._id) === String(_id) ? null : sp,
      );
    };

    socket.on("project:added", onAdded);
    socket.on("project:removed", onRemoved);
    return () => {
      socket.off("project:added", onAdded);
      socket.off("project:removed", onRemoved);
    };
  }, []);

  /* ---------- realtime: join chat room + incoming ---------- */
  useEffect(() => {
    if (!selectedProject?._id) return;
    const socket = getSocket();
    if (!socket) return;

    socket.emit("project:join", { projectId: selectedProject._id });

    const appendToCache = (projId, entry, createdAtISO) => {
      const grouped = cacheRef.current.get(projId) || {};
      const key = dayjs(createdAtISO).format("YYYY-MM-DD");

      // already have this exact message -> skip
      const alreadyThere = Object.values(grouped).some((day) =>
        day.some((m) => String(m._id) === String(entry._id)),
      );
      if (alreadyThere) return;

      // remove my own temporary (optimistic) copy of this message
      if (String(entry.senderId || "") === String(authUser?._id || "")) {
        for (const k of Object.keys(grouped)) {
          const idx = grouped[k].findIndex(
            (m) =>
              m._optimistic &&
              String(m.text || "").trim() === String(entry.text || "").trim(),
          );
          if (idx !== -1) {
            grouped[k] = grouped[k].filter((_, i) => i !== idx);
            break;
          }
        }
      }

      const arr = grouped[key] ? [...grouped[key], entry] : [entry];
      grouped[key] = arr.slice(-Math.max(PAGE_SIZE, 1));
      const keys = Object.keys(grouped).sort();
      if (keys.length > 10) delete grouped[keys[0]];
      cacheRef.current.set(projId, grouped);
      if (selectedProject && String(selectedProject._id) === projId) {
        setChatMessages(applyFileSizes(grouped));
      }
    };

    const onIncoming = (msg) => {
      const projId = String(msg.project || "");
      const createdAtISO = msg.createdAt || new Date().toISOString();

      const entry = {
        _id: msg._id,
        sender:
          msg?.sender?.name ||
          msg?.sender?.username ||
          msg?.sender?.email ||
          msg?.senderLabel ||
          "System",
        senderId:
          msg?.sender?._id || msg?.senderId?._id || msg?.senderId || null,
        text: msg.content,
        file: msg.file,
        fileName: msg.fileName,
        fileSize: msg.fileSize,
        type: inferType(msg),
        task:
          msg.type === "task"
            ? {
                assignedToId: msg.assignedToId || msg.assignedTo,
                assignedToName: msg.assignedToName,
                typeofProject: msg.typeofProject,
                workType: msg.workType,
                drawingDocuments: msg.drawingDocuments,
                estimatedHours: msg.estimatedHours,
                dueDate: msg.dueDate,
                remark: msg.remark,
              }
            : null,
        decision: msg.decision || null,
        replyTo: null,
        time: new Date(createdAtISO).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        createdAt: createdAtISO,
      };

      appendToCache(projId, entry, createdAtISO);

      showProjectDesktopNotification({
        message: msg,
        project: selectedProject,
        myUserId: authUser?._id,
      });
    };

    socket.on("project:message", onIncoming);

    return () => {
      socket.off("project:message", onIncoming);
      socket.emit("project:leave", { projectId: selectedProject._id });
    };
  }, [selectedProject?._id, applyFileSizes, selectedProject, authUser?._id]);

  /* ---------- optimistic UI for text/file ---------- */
  const addOptimistic = useCallback(
    (payload = {}) => {
      const createdAtISO = new Date().toISOString();
      const key = dayjs(createdAtISO).format("YYYY-MM-DD");

      const optimistic = {
        _id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        sender: "You",
        senderId: authUser?._id || null,
        text: payload.text || "",
        file: payload.file || null,
        fileName: payload.fileName || null,
        fileSize: payload.fileSize || null,
        type: payload.type || null,
        decision: payload.decision || null,
        replyTo: payload.replyTo
          ? {
              sender: payload.replyTo.sender,
              text: payload.replyTo.text,
              fileName: payload.replyTo.fileName,
              _id: payload.replyTo._id,
            }
          : null,
        time: new Date(createdAtISO).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        createdAt: createdAtISO,
        _optimistic: true,
      };

      if (selectedProject?._id) {
        const grouped = cacheRef.current.get(selectedProject._id) || {};
        const arr = grouped[key] ? [...grouped[key], optimistic] : [optimistic];
        grouped[key] = arr.slice(-PAGE_SIZE);
        cacheRef.current.set(selectedProject._id, grouped);
        setChatMessages(applyFileSizes(grouped));
      }

      requestAnimationFrame(() =>
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }),
      );
    },
    [authUser?._id, selectedProject?._id, applyFileSizes],
  );

  /* ---------- actions (post / upload / download) ---------- */
  const [replyingTo, setReplyingTo] = useState(null);

  const insertMention = useCallback(
    (member) => {
      if (!inputRef.current) return;
      const el = inputRef.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;

      const probe = matchMentionAtCursor(message, start);

      const displayToken = `@${member.name}`;
      const rawToken = makeMentionToken(member.name, member._id);

      let before = message.slice(0, start);
      let after = message.slice(end);

      let beforeRaw = messageRaw.slice(0, start);
      let afterRaw = messageRaw.slice(end);

      if (probe) {
        before = message.slice(0, probe.start);
        after = message.slice(start);
        beforeRaw = messageRaw.slice(0, probe.start);
        afterRaw = messageRaw.slice(start);
      }

      const nextDisplay = `${before}${displayToken} ${after}`;
      const nextRaw = `${beforeRaw}${rawToken} ${afterRaw}`;

      setMessage(nextDisplay);
      setMessageRaw(nextRaw);
      setMentionOpen(false);
      setMentionQuery("");
      setMentionIndex(0);

      const caret = (before + displayToken + " ").length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    },
    [message, messageRaw],
  );

  const handlePost = async () => {
    if (!selectedProject) return;

    /* ----- DECISION MODE ----- */
    if (postAsDecision && !replyingTo) {
      if (!decisionTopic.trim() && !decisionFinal.trim()) return;

      addOptimistic({
        text: `Topic: ${decisionTopic}\n\nFinal Decision:\n${decisionFinal}`,
        type: "decision",
        decision: {
          topic: decisionTopic,
          finalDecision: decisionFinal,
          decideBy: decisionDecideBy || null,
          lock: !!decisionLock,
          byName: authUser?.name || "",
          lockedBy: decisionLock ? authUser?.name || "" : "",
        },
        file: decisionFile ? URL.createObjectURL(decisionFile) : null,
        fileName: decisionFile?.name || null,
        fileSize: decisionFile?.size || null,
      });

      let uploaded = null;
      try {
        if (decisionFile) {
          const fd = new FormData();
          fd.append("file", decisionFile);
          uploaded = await axiosInstance.post(
            `/project-messages/${selectedProject._id}/upload`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        }

        const rawBody = displayToRaw(decisionFinal, projectMembers);
        const mentionIds = extractMentionIds(rawBody);

        const payload = {
          type: "decision",
          content:
            `Topic: ${decisionTopic}\n\n` +
            `Final Decision:\n${rawBody}\n` +
            (decisionDecideBy ? `\nDecideBy: ${decisionDecideBy}` : "") +
            `\nLock: ${decisionLock ? "true" : "false"}`,
          decision: {
            topic: decisionTopic || "",
            finalDecision: rawBody || "",
            decideBy: decisionDecideBy || null,
            lock: !!decisionLock,
            byName: authUser?.name || "",
            lockedBy: decisionLock ? authUser?.name || "" : "",
          },
          file: uploaded?.data?.fileKey || null,
          fileName: uploaded?.data?.fileName || null,
          fileSize: uploaded?.data?.fileSize || null,
          replyTo: null,
          mentions: mentionIds,
          notificationRecipientIds,
        };

        await axiosInstance.post(
          `/project-messages/${selectedProject._id}/messages`,
          payload,
        );
      } catch (e) {
        console.error("Error posting decision:", e);
      } finally {
        setDecisionTopic("");
        setDecisionFinal("");
        setDecisionDecideBy("");
        setDecisionLock(false);
        setDecisionFile(null);
      }
      return;
    }

    /* ----- NORMAL/DISCUSSION MODE ----- */
    if (!message.trim()) return;
    const type = postAsDiscussion ? "discussion" : undefined;

    const contentRaw =
      postAsDiscussion && discussionTopic && !replyingTo
        ? `Topic: ${discussionTopic}\n\n${
            messageRaw || displayToRaw(message, projectMembers)
          }`
        : messageRaw || displayToRaw(message, projectMembers);

    const mentionIds = extractMentionIds(contentRaw);

    if (!editingMessageId) {
      addOptimistic({ text: contentRaw, type, replyTo: replyingTo });
    }

    try {
      if (editingMessageId) {
        await axiosInstance.put(
          `/project-messages/${selectedProject._id}/messages/${editingMessageId}`,
          {
            content: contentRaw,
            type,
            replyTo: replyingTo?._id || null,
            mentions: mentionIds,
            notificationRecipientIds,
          },
        );
        setEditingMessageId(null);
      } else {
        await axiosInstance.post(
          `/project-messages/${selectedProject._id}/messages`,
          {
            content: contentRaw,
            type,
            replyTo: replyingTo?._id || null,
            mentions: mentionIds,
            notificationRecipientIds,
          },
        );
      }

      setMessage("");
      setMessageRaw("");
      setDiscussionTopic("");
      setEmojiOpen(false);
      setReplyingTo(null);
      setMentionOpen(false);
      setMentionQuery("");
      setMentionIndex(0);
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.blur();
        requestAnimationFrame(() => {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(0, 0);
        });
      }
    } catch (e) {
      console.error(e);
      setTimeout(
        () => fetchMessages(selectedProject._id, { skipIfCached: false }),
        400,
      );
    }
  };

  const uploadAndPostFile = async (file) => {
    if (!file || !selectedProject) return;

    if (postAsDecision && !replyingTo) {
      setDecisionFile(file);
      return;
    }

    const baseCaption = `📎 ${file.name}`;
    const caption =
      postAsDiscussion && discussionTopic && !replyingTo
        ? `Topic: ${discussionTopic}\n\n${baseCaption}`
        : baseCaption;

    addOptimistic({
      text: caption,
      file: "__uploading__",
      fileName: file.name,
      fileSize: file.size,
      replyTo: replyingTo,
      type: postAsDiscussion && !replyingTo ? "discussion" : undefined,
    });

    const fd = new FormData();
    fd.append("file", file);

    try {
      const up = await axiosInstance.post(
        `/project-messages/${selectedProject._id}/upload`,
        fd,
      );

      const serverCaption =
        postAsDiscussion && discussionTopic && !replyingTo
          ? `Topic: ${discussionTopic}\n\n📎 ${up.data.fileName}`
          : `📎 ${up.data.fileName}`;

      await axiosInstance.post(
        `/project-messages/${selectedProject._id}/messages`,
        {
          content: serverCaption,
          file: up.data.fileKey,
          fileName: up.data.fileName,
          fileSize: up.data.fileSize,
          replyTo: replyingTo?._id || null,
          type: postAsDiscussion && !replyingTo ? "discussion" : undefined,
          mentions: [],
          notificationRecipientIds,
        },
      );
    } catch (e) {
      console.error(e);
      alert("❌ File upload failed.");
    } finally {
      setReplyingTo(null);
      setDiscussionTopic("");
      setTimeout(
        () => fetchMessages(selectedProject._id, { skipIfCached: false }),
        200,
      );
    }
  };

  const uploadMany = async (files) => {
    for (const f of files) {
      await uploadAndPostFile(f);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (postAsDecision && !replyingTo) {
      setDecisionFile(files[0]);
    } else {
      await uploadMany(files);
    }
    e.target.value = "";
  };

   const triggerDownload = async (fileKey, fileName) => {
    try {
      const url =
        `${SERVER_ORIGIN}/api/upload/proxy-download?url=` +
        `${encodeURIComponent(fileKey)}&name=${encodeURIComponent(fileName || "")}&inline=0`;

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Download error:", err);
      alert("❌ Download failed");
    }
  };

  /* ---------- dnd handlers ---------- */
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedProject) return;
    dragCounter.current += 1;
    setDragActive(true);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedProject) return;
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedProject) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      setDragActive(false);
      dragCounter.current = 0;
    }
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragActive(false);
    if (!selectedProject) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    await uploadMany(files);
  };

  // Paste-to-upload
  const onPaste = async (e) => {
    if (!selectedProject) return;
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault();
      await uploadMany(files);
    }
  };

  /* ---------- file size lazy fetch ---------- */
  const ensureSize = async (msg) => {
    if (msg.fileSize || !msg.file || fileSizeCache.current.has(msg._id)) return;
    const size = await getFileSizeFromURL(msg.file);
    if (size) {
      fileSizeCache.current.set(msg._id, size);
      setChatMessages((prev) => {
        const out = {};
        for (const k of Object.keys(prev)) {
          out[k] = prev[k].map((m) =>
            m._id === msg._id ? { ...m, fileSizeDisplay: size } : m,
          );
        }
        return out;
      });
    }
  };

  /* ---------- message context menu ---------- */
  const openMsgMenu = (e, msg) => {
    e.preventDefault();
    const opts = [
      { label: "Reply", action: () => setReplyingTo(msg) },
      {
        label: "Copy",
        action: async () => {
          const text = msg.text || msg.fileName || "";
          try {
            await navigator.clipboard.writeText(text);
          } catch {}
        },
      },
    ];

    if (msg.file) {
      opts.push({
        label: "Download",
        action: () => triggerDownload(msg.file, msg.fileName),
      });
    }
    createContextMenu(e.clientX, e.clientY, opts);
  };

  /* ---------- build thread index ---------- */
  const { repliesIndex, hiddenIds } = useMemo(() => {
    const index = new Map();
    const hide = new Set();
    const all = [];
    Object.values(chatMessages).forEach((arr) => all.push(...arr));

    const isRoot = (m) =>
      (m.type === "discussion" ||
        (looksLikeDiscussionText(m.text) && !looksLikeDecisionText(m.text))) &&
      !m.replyTo;

    const byId = new Map(all.map((m) => [m._id, m]));

    all.forEach((m) => {
      if (m.replyTo && m.replyTo._id && byId.has(m.replyTo._id)) {
        const parent = byId.get(m.replyTo._id);
        if (isRoot(parent)) {
          if (!index.has(parent._id)) index.set(parent._id, []);
          index.get(parent._id).push(m);
          hide.add(m._id);
        }
      }
    });

    for (const [, list] of index) {
      list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }
    return { repliesIndex: index, hiddenIds: hide };
  }, [chatMessages]);

  /* ---------- render ---------- */
  const mentionChoices = useMemo(() => {
    const q = (mentionQuery || "").toLowerCase();
    const list = projectMembers || [];
    if (!q) return list.slice(0, 8);
    return list.filter((m) => m.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, projectMembers]);

  const projectTitle = selectedProject?.projectName || selectedProject?.name;

  return (
    <div
      ref={rootRef}
      className={`pc-root${isDesktop ? " pc-root--desktop" : ""}`}
    >
      {/* Left: Projects */}
      <div
        className={`pc-left${
          isMobile && selectedProject ? " pc-left--hidden" : ""
        }`}
      >
        <ProjectsSidebar
          projects={projects}
          loading={loading}
          selectedProject={selectedProject}
          setSelectedProject={(p) =>
            startTransition(() => setSelectedProject(p))
          }
          refreshProjects={fetchProjects}
          isMobile={isMobile}
          onVisibleProjectsChange={(list) => {
            setVisibleProjects(list || []);
            setTimeout(prefetchVisible, 0);
          }}
          initialOpenProjectId={incomingProjectId}
          initialOpenProjectName={incomingProjectName}
        />
      </div>

      {/* Center: Chat (full width now) */}
      <div
        className="pc-center"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="pc-header">
          {isMobile && selectedProject && (
            <button
              type="button"
              className="pc-back"
              onClick={() => {
                userNavigatedBack.current = true;
                startTransition(() => setSelectedProject(null));

                setTimeout(() => {
                  userNavigatedBack.current = false;
                }, 600);
              }}
              aria-label="Back to projects"
            >
              ←
            </button>
          )}

          {selectedProject ? (
            <>
              <div className="pc-header-avatar">
                {String(projectTitle || "P")
                  .charAt(0)
                  .toUpperCase()}
              </div>
              <div className="pc-header-text">
                <div className="pc-header-title">{projectTitle}</div>
                <div className="pc-header-sub">
                  {members.length} member{members.length === 1 ? "" : "s"}
                </div>
              </div>
            </>
          ) : (
            <div className="pc-header-title">
              Select a project to start chatting
            </div>
          )}
        </div>

        {dragActive && selectedProject && (
          <div className="pc-drop">Drop files to upload</div>
        )}

        {!selectedProject ? (
          <div className="pc-empty">
            <div className="pc-empty-icon">💬</div>
            No project selected
          </div>
        ) : (
          <>
            <div
              className={`pc-messages${isMobile ? " pc-messages--mobile" : ""}`}
            >
              {messagesLoading && Object.keys(chatMessages).length === 0 && (
                <>
                  <div className="pc-skeleton" />
                  <div className="pc-skeleton" />
                  <div className="pc-skeleton" />
                </>
              )}

              {Object.keys(chatMessages).map((dateKey) => (
                <div key={dateKey}>
                  <div className="pc-date">
                    <span>{formatDateGroup(dateKey)}</span>
                  </div>

                  {chatMessages[dateKey]
                    .filter((msg) => !hiddenIds.has(msg._id))
                    .map((msg) => {
                      const isMe =
                        String(msg.senderId || "") ===
                        String(authUser?._id || "");
                      const thread =
                        (msg.type === "discussion" ||
                          looksLikeDiscussionText(msg.text)) &&
                        !msg.replyTo
                          ? repliesIndex.get(msg._id) || []
                          : [];
                      return (
                        <MessageRow
                          key={msg._id}
                          msg={msg}
                          isMe={isMe}
                          isMobile={isMobile}
                          onContext={openMsgMenu}
                          ensureSize={ensureSize}
                          triggerDownload={triggerDownload}
                          authUserId={authUser?._id}
                          onReply={(m) => setReplyingTo(m)}
                          threadReplies={thread}
                        />
                      );
                    })}
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            <div className={`pc-footer${isMobile ? " pc-footer--mobile" : ""}`}>
              {replyingTo && (
                <div className="pc-replybar">
                  Replying to <strong>{replyingTo.sender}</strong>:{" "}
                  {replyingTo.text || replyingTo.fileName || "Discussion"}
                  <span
                    className="pc-replybar-x"
                    onClick={() => setReplyingTo(null)}
                  >
                    ❌
                  </span>
                </div>
              )}

              {/* Decision composer */}
              {postAsDecision && !replyingTo && (
                <div className="pc-composer-card">
                  <div className="pc-composer-title">🧭 Decision</div>

                  <label className="pc-label">Topic</label>
                  <input
                    value={decisionTopic}
                    onChange={(e) => setDecisionTopic(e.target.value)}
                    placeholder="Write topic..."
                    className="pc-field"
                  />

                  <div className="pc-composer-by">
                    By: <span>{authUser?.name || "—"}</span>
                  </div>

                  <label className="pc-label">Final Decision</label>
                  <textarea
                    value={decisionFinal}
                    onChange={(e) => setDecisionFinal(e.target.value)}
                    placeholder="Type the final decision..."
                    rows={3}
                    className="pc-field pc-field--textarea"
                  />

                  <div className="pc-composer-row">
                    <div>
                      <div className="pc-label">Decide by</div>
                      <input
                        type="date"
                        value={decisionDecideBy}
                        onChange={(e) => setDecisionDecideBy(e.target.value)}
                        className="pc-date-input"
                      />
                    </div>

                    <label
                      className={`pc-lock${decisionLock ? " pc-lock--on" : ""}`}
                      title="Lock this decision"
                    >
                      <input
                        type="checkbox"
                        checked={decisionLock}
                        onChange={(e) => setDecisionLock(e.target.checked)}
                      />
                      🔒 Lock
                    </label>

                    {decisionLock && (
                      <span className="pc-lockedby">
                        Locked by: {authUser?.name || "—"}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="pc-filebtn"
                    title="Attach file"
                  >
                    📎 File
                  </button>
                  {decisionFile && (
                    <span className="pc-filename">{decisionFile.name}</span>
                  )}
                </div>
              )}

              {/* Discussion topic input */}
              {postAsDiscussion && !replyingTo && (
                <div className="pc-topic-row">
                  <input
                    value={discussionTopic}
                    onChange={(e) => setDiscussionTopic(e.target.value)}
                    placeholder="Discussion topic (optional)"
                    className="pc-field"
                  />
                </div>
              )}

              <div className="pc-inputrow">
                {/* Emoji panel */}
                <div
                  ref={emojiRef}
                  className={`pc-emoji${emojiOpen ? " pc-emoji--open" : ""}`}
                >
                  <div className="pc-emoji-grid">
                    {EMOJIS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => {
                          setMessage((m) => m + e);
                          setMessageRaw((r) => r + e);
                          inputRef.current?.focus();
                        }}
                        className="pc-emoji-btn"
                        title={e}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mentions dropdown */}
                <MentionList
                  open={mentionOpen}
                  items={mentionChoices}
                  activeIndex={mentionIndex}
                  onPick={insertMention}
                  style={{
                    bottom: mentionMenuPos.bottom,
                    right: mentionMenuPos.right,
                  }}
                />

                {/* Main text input */}
                <input
                  ref={inputRef}
                  name="project-chat-message"
                  autoComplete="off"
                  value={message}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMessage(val);
                    setMessageRaw(displayToRaw(val, projectMembers));

                    const caret = e.target.selectionStart || 0;
                    const probe = matchMentionAtCursor(val, caret);
                    if (probe) {
                      setMentionOpen(true);
                      setMentionQuery(probe.query || "");
                      setMentionIndex(0);
                      setMentionMenuPos({ bottom: 56, right: 92 });
                    } else {
                      setMentionOpen(false);
                      setMentionQuery("");
                      setMentionIndex(0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (mentionOpen) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setMentionIndex((i) =>
                          Math.min(
                            i + 1,
                            Math.max(mentionChoices.length - 1, 0),
                          ),
                        );
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setMentionIndex((i) => Math.max(i - 1, 0));
                        return;
                      }
                      if (e.key === "Enter") {
                        const item = mentionChoices[mentionIndex];
                        if (item) {
                          e.preventDefault();
                          insertMention(item);
                          return;
                        }
                      }
                      if (e.key === "Escape") {
                        setMentionOpen(false);
                        setMentionQuery("");
                        setMentionIndex(0);
                        return;
                      }
                    }

                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handlePost();
                    }
                  }}
                  onPaste={onPaste}
                  placeholder={
                    postAsDecision
                      ? "You’re composing a Decision above…"
                      : replyingTo
                        ? "Reply to discussion..."
                        : "Type a message..."
                  }
                  className="pc-input"
                  disabled={postAsDecision && !replyingTo}
                />

                {/* Discussion + Decision + Assign Task */}
                <div className="pc-toggles">
                  <button
                    type="button"
                    className={`pc-toggle${postAsDiscussion ? " pc-toggle--on" : ""}`}
                    onClick={() => {
                      setPostAsDiscussion((s) => !s);
                      if (!postAsDiscussion) setPostAsDecision(false);
                    }}
                    title="Post as Discussion"
                  >
                    🗣️ Discussion
                  </button>

                  <button
                    type="button"
                    className={`pc-toggle${postAsDecision ? " pc-toggle--on" : ""}`}
                    onClick={() => {
                      setPostAsDecision((s) => !s);
                      if (!postAsDecision) setPostAsDiscussion(false);
                    }}
                    title="Post as Decision"
                  >
                    🧭 Decision
                  </button>

                  <button
                    type="button"
                    className="pc-toggle pc-toggle--task"
                    onClick={() => setAssignTaskOpen(true)}
                    title="Assign Task"
                  >
                    ✅ Assign Task
                  </button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  multiple={!postAsDecision || !!replyingTo}
                />

                {/* Emoji */}
                <button
                  type="button"
                  onClick={() => setEmojiOpen((s) => !s)}
                  className="pc-iconbtn"
                  title="Emoji"
                >
                  <Smile size={18} />
                </button>

                {/* Attach file */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="pc-iconbtn"
                  title="Attach file"
                >
                  <FileText size={18} />
                </button>

                <button type="button" onClick={handlePost} className="pc-send">
                  {editingMessageId ? "Update" : "Send"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Assign Task Modal */}
      {assignTaskOpen && selectedProject && (
        <AssignTaskModal
          project={selectedProject}
          onClose={() => setAssignTaskOpen(false)}
          onAssigned={handleTaskAssigned}
          users={[]}
          drawingMaster={[]}
        />
      )}
    </div>
  );
};

export default ProjectCommunication;